import { useState, useEffect } from 'react';
import axios from 'axios';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CurrencyDollar, ShoppingBag, Calendar, Eye } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// === Helpers TZ-correct ===
// Local date string (YYYY-MM-DD) of given Date or today
const toLocalDateStr = (d = new Date()) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Convert YYYY-MM-DD to ISO at LOCAL start/end of day
const dateStrToISO = (dateStr, endOfDay = false) => {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0);
  return date.toISOString();
};

const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const formatMoney = (n) => `$${(Number(n) || 0).toFixed(2)}`;

export const SellerCommissions = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = toLocalDateStr();
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [salesDialog, setSalesDialog] = useState({ open: false, seller: null, sales: [], loading: false });
  const [breakdownDlg, setBreakdownDlg] = useState({ open: false, seller: null, data: null, loading: false });
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const downloadAllSellersPdf = async () => {
    setDownloadingPdf(true);
    try {
      const params = new URLSearchParams({
        start_date: dateStrToISO(startDate),
        end_date: dateStrToISO(endDate, true),
      });
      const resp = await axios.get(`${API}/reports/all-sellers-pdf?${params.toString()}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `comisiones_${startDate}_a_${endDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al generar PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const openBreakdown = async (seller) => {
    setBreakdownDlg({ open: true, seller, data: null, loading: true });
    try {
      const params = new URLSearchParams({
        start_date: dateStrToISO(startDate),
        end_date: dateStrToISO(endDate, true),
      });
      const { data } = await axios.get(
        `${API}/reports/seller-item-breakdown/${seller.seller_id}?${params.toString()}`,
        { withCredentials: true }
      );
      setBreakdownDlg({ open: true, seller, data, loading: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al generar reporte');
      setBreakdownDlg({ open: true, seller, data: null, loading: false });
    }
  };

  useEffect(() => {
    // Debounce leve (350ms) para evitar múltiples fetches al teclear la fecha
    const handle = setTimeout(() => { fetchData(); }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const now = new Date();
      const todayStr = toLocalDateStr(now);
      const monthStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0); // último día del mes
      const params = new URLSearchParams({
        start_date: dateStrToISO(startDate),
        end_date: dateStrToISO(endDate, true),
        // "día" siempre = HOY local, según pedido del usuario ("ventas de hoy")
        day_start: dateStrToISO(todayStr),
        day_end: dateStrToISO(todayStr, true),
        // "mes" siempre = mes en curso del usuario
        month_start: dateStrToISO(toLocalDateStr(monthStartDate)),
        month_end: dateStrToISO(toLocalDateStr(monthEndDate), true),
        day_label: now.toLocaleDateString('es-MX'),
        month_label: `${monthNames[now.getMonth()]} ${now.getFullYear()}`
      });
      const { data } = await axios.get(`${API}/reports/sellers-performance?${params.toString()}`, { withCredentials: true });
      // Ordenar por comisión del rango (mayor primero) para destacar a los top
      const sorted = [...data].sort((a, b) =>
        (Number(b.range_commission) || 0) - (Number(a.range_commission) || 0)
      );
      setData(sorted);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar reportes');
    } finally {
      setLoading(false);
    }
  };

  const setQuickRange = (range) => {
    const t = new Date();
    if (range === 'today') {
      const d = toLocalDateStr(t);
      setStartDate(d); setEndDate(d);
    } else if (range === 'week') {
      const start = new Date(t); start.setDate(t.getDate() - 6);
      setStartDate(toLocalDateStr(start)); setEndDate(toLocalDateStr(t));
    } else if (range === 'month') {
      const start = new Date(t.getFullYear(), t.getMonth(), 1);
      setStartDate(toLocalDateStr(start)); setEndDate(toLocalDateStr(t));
    } else if (range === 'all') {
      setStartDate('2020-01-01'); setEndDate(toLocalDateStr(t));
    }
  };

  const openSalesDialog = async (seller) => {
    setSalesDialog({ open: true, seller, sales: [], loading: true });
    try {
      const params = new URLSearchParams({
        start_date: dateStrToISO(startDate),
        end_date: dateStrToISO(endDate, true)
      });
      const { data } = await axios.get(`${API}/sales/by-seller/${seller.seller_id}?${params.toString()}`, { withCredentials: true });
      setSalesDialog({ open: true, seller, sales: data, loading: false });
    } catch (error) {
      toast.error('Error al cargar ventas');
      setSalesDialog({ open: true, seller, sales: [], loading: false });
    }
  };

  const isSingleDay = startDate === endDate;
  const isToday = isSingleDay && startDate === today;

  return (
    <MainLayout>
      <div className="p-8">
        <div className="mb-6">
          <h1 className="text-4xl font-bold tracking-tight">Comisiones de Vendedores</h1>
          <p className="text-muted-foreground mt-1">
            {isToday ? 'Mostrando comisiones acumuladas del día' : `Rango: ${startDate} → ${endDate}`}
          </p>
        </div>

        {/* Date controls */}
        <div className="bg-white border border-border rounded-sm p-4 mb-6">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label>Desde</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="start-date-input" />
            </div>
            <div>
              <Label>Hasta</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="end-date-input" />
            </div>
            <Button
              size="sm"
              onClick={() => fetchData()}
              disabled={loading}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="apply-date-range-btn"
            >
              {loading ? 'Cargando…' : '🔄 Aplicar'}
            </Button>
            <div className="flex gap-2 flex-wrap ml-auto">
              <Button variant="outline" size="sm" onClick={() => setQuickRange('today')} data-testid="range-today">Hoy</Button>
              <Button variant="outline" size="sm" onClick={() => setQuickRange('week')} data-testid="range-week">7 días</Button>
              <Button variant="outline" size="sm" onClick={() => setQuickRange('month')} data-testid="range-month">Mes</Button>
              <Button variant="outline" size="sm" onClick={() => setQuickRange('all')} data-testid="range-all">Todo</Button>
              <Button
                size="sm"
                onClick={downloadAllSellersPdf}
                disabled={downloadingPdf}
                data-testid="download-all-pdf"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {downloadingPdf ? 'Generando…' : '📄 PDF Todos'}
              </Button>
            </div>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-6 text-sm text-muted-foreground gap-2" data-testid="loading-indicator">
            <div className="w-4 h-4 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
            <span>Cargando comisiones del rango {startDate} → {endDate}…</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((s) => (
            <div
              key={s.seller_id}
              className="bg-white border border-border rounded-sm p-5 space-y-3 hover:shadow-md hover:border-primary cursor-pointer btn-transition"
              data-testid={`seller-perf-${s.seller_id}`}
              onClick={() => openSalesDialog(s)}
            >
              <div className="flex justify-between items-start">
                <h3 className="font-semibold text-lg hover:underline">{s.seller_name}</h3>
                <div className="flex items-center gap-2 text-xs">
                  <button
                    onClick={(e) => { e.stopPropagation(); openBreakdown(s); }}
                    className="text-violet-700 hover:bg-violet-50 px-2 py-0.5 rounded border border-violet-300 transition"
                    data-testid={`seller-breakdown-btn-${s.seller_id}`}
                    title="Reporte por ítem con comisiones"
                  >
                    📊 Reporte
                  </button>
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Eye size={14} />
                    Ver ventas
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-emerald-50 p-2 rounded">
                  <div className="flex items-center gap-1 text-emerald-700 text-xs">
                    <CurrencyDollar size={14} weight="fill" />
                    Día ({s.day_label})
                  </div>
                  <p className="text-xl font-bold text-emerald-700">{formatMoney(s.day_commission)}</p>
                </div>
                <div className="bg-blue-50 p-2 rounded">
                  <div className="flex items-center gap-1 text-blue-700 text-xs">
                    <Calendar size={14} weight="fill" />
                    Mes
                  </div>
                  <p className="text-xl font-bold text-blue-700">{formatMoney(s.month_commission)}</p>
                </div>
              </div>

              <div className="text-xs text-muted-foreground border-t pt-2 space-y-1">
                <div className="flex justify-between">
                  <span>Ventas hoy:</span>
                  <span className="font-semibold text-foreground">{s.sales_today_count}</span>
                </div>
                {!isToday && (
                  <>
                    <div className="flex justify-between">
                      <span>Ventas en rango:</span>
                      <span className="font-semibold text-foreground">{s.sales_range_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Comisión del rango:</span>
                      <span className="font-semibold text-emerald-700">{formatMoney(s.range_commission)}</span>
                    </div>
                  </>
                )}
              </div>

              {s.products.length > 0 ? (
                <div onClick={(e) => e.stopPropagation()}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Productos vendidos en el rango</p>
                  <div className="border rounded text-xs max-h-40 overflow-y-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-2 py-1 text-left font-semibold">Producto</th>
                          <th className="px-2 py-1 text-right font-semibold">Cant.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.products.map((p) => (
                          <tr key={p.product_id} className="border-t">
                            <td className="px-2 py-1 truncate max-w-[160px]">{p.product_name}</td>
                            <td className="px-2 py-1 text-right">{p.quantity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">Sin ventas en el rango.</p>
              )}
            </div>
          ))}
          {!loading && data.length === 0 && (
            <p className="col-span-full text-center text-muted-foreground py-8">
              No hay vendedores activos.
            </p>
          )}
        </div>
      </div>

      {/* Sales Detail Dialog */}
      <Dialog open={salesDialog.open} onOpenChange={(o) => !o && setSalesDialog({ open: false, seller: null, sales: [], loading: false })}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Ventas de {salesDialog.seller?.seller_name}
              <span className="text-sm font-normal text-muted-foreground ml-2">
                {startDate} → {endDate}
              </span>
            </DialogTitle>
          </DialogHeader>
          {salesDialog.loading ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Cargando ventas...</p>
          ) : salesDialog.sales.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No hay ventas en este rango.</p>
          ) : (
            <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-3 gap-2 text-sm bg-slate-50 p-3 rounded">
                <div>
                  <p className="text-xs text-muted-foreground">Total ventas</p>
                  <p className="text-lg font-bold">{salesDialog.sales.length}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total facturado</p>
                  <p className="text-lg font-bold">{formatMoney(salesDialog.sales.reduce((a, s) => a + (s.total || 0), 0))}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Comisión total</p>
                  <p className="text-lg font-bold text-emerald-700">
                    {formatMoney(salesDialog.sales.reduce((a, s) => a + (s.items || []).reduce((b, it) => b + (it.commission || 0) * (it.quantity || 0), 0), 0))}
                  </p>
                </div>
              </div>

              {salesDialog.sales.map((sale) => (
                <div key={sale.id} className="border rounded p-3 text-sm" data-testid={`sale-${sale.id}`}>
                  <div className="flex justify-between mb-2">
                    <div>
                      <p className="font-medium">
                        {new Date(sale.created_at).toLocaleString('es-MX')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {sale.shipment_folio ? `Envío ${sale.shipment_folio}` : 'Venta directa'} · {sale.payment_method}
                      </p>
                    </div>
                    <p className="font-bold">{formatMoney(sale.total)}</p>
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-muted-foreground border-b">
                        <th className="text-left py-1">Producto</th>
                        <th className="text-right py-1">Cant.</th>
                        <th className="text-right py-1">P. Unit</th>
                        <th className="text-right py-1">Subtotal</th>
                        <th className="text-right py-1">Comisión</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(sale.items || []).map((it, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-1">{it.product_name}</td>
                          <td className="text-right py-1">{it.quantity}</td>
                          <td className="text-right py-1">{formatMoney(it.unit_price)}</td>
                          <td className="text-right py-1">{formatMoney(it.unit_price * it.quantity)}</td>
                          <td className="text-right py-1 text-emerald-600">{formatMoney((it.commission || 0) * it.quantity)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Breakdown por ítem */}
      <Dialog open={breakdownDlg.open} onOpenChange={(o) => !o && setBreakdownDlg({ open: false, seller: null, data: null, loading: false })}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              📊 Reporte por ítem · {breakdownDlg.seller?.seller_name}
              <span className="text-sm font-normal text-muted-foreground ml-2">
                {startDate} → {endDate}
              </span>
            </DialogTitle>
          </DialogHeader>
          {breakdownDlg.loading ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Generando reporte...</p>
          ) : !breakdownDlg.data ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Sin datos.</p>
          ) : (
            <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
              {/* Stats motivacionales */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div className="bg-emerald-50 border border-emerald-200 p-3 rounded">
                  <p className="text-xs text-emerald-700">💰 Comisión total</p>
                  <p className="text-2xl font-bold text-emerald-700">
                    {formatMoney(breakdownDlg.data.totals.total_commission)}
                  </p>
                </div>
                <div className="bg-sky-50 border border-sky-200 p-3 rounded">
                  <p className="text-xs text-sky-700">🛒 Ventas en rango</p>
                  <p className="text-2xl font-bold text-sky-700">
                    {breakdownDlg.data.totals.sales_count}
                  </p>
                  <p className="text-[10px] text-sky-700">
                    {breakdownDlg.data.totals.days_with_sales} día(s) con ventas
                  </p>
                </div>
                <div className="bg-amber-50 border border-amber-200 p-3 rounded">
                  <p className="text-xs text-amber-700">📈 Promedio diario</p>
                  <p className="text-lg font-bold text-amber-700">
                    {breakdownDlg.data.totals.avg_sales_per_day} ventas/día
                  </p>
                  <p className="text-[10px] text-amber-700">
                    {formatMoney(breakdownDlg.data.totals.avg_commission_per_day)} de comisión
                  </p>
                </div>
                <div className="bg-violet-50 border border-violet-200 p-3 rounded">
                  <p className="text-xs text-violet-700">⭐ Producto top</p>
                  <p className="text-sm font-bold text-violet-700 leading-tight">
                    {breakdownDlg.data.totals.top_product || '—'}
                  </p>
                  <p className="text-[10px] text-violet-700 mt-1">
                    {formatMoney(breakdownDlg.data.totals.avg_commission_per_sale)} prom. por venta
                  </p>
                </div>
              </div>

              {/* Mensaje motivacional */}
              {breakdownDlg.data.totals.total_commission > 0 && (
                <div className="bg-gradient-to-r from-emerald-50 to-sky-50 border border-emerald-200 rounded p-3 text-sm">
                  <p className="font-semibold text-emerald-800">
                    🎉 ¡Buen trabajo {breakdownDlg.data.seller_name}!
                  </p>
                  <p className="text-xs text-slate-700 mt-1">
                    Llevas <b>{breakdownDlg.data.totals.total_quantity}</b> piezas vendidas en{' '}
                    <b>{breakdownDlg.data.totals.days_with_sales}</b> día(s) activo(s).
                    A este ritmo cierras el mes con aproximadamente{' '}
                    <b className="text-emerald-700">
                      {formatMoney(breakdownDlg.data.totals.avg_commission_per_day * 30)}
                    </b>{' '}
                    en comisiones. ¡Sigue así!
                  </p>
                </div>
              )}

              {/* Tabla de ítems */}
              <div className="border rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Producto</th>
                      <th className="px-3 py-2 text-right">Comisión c/u</th>
                      <th className="px-3 py-2 text-right">Vendidos</th>
                      <th className="px-3 py-2 text-right">Comisión total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakdownDlg.data.rows.length === 0 ? (
                      <tr><td colSpan={4} className="text-center text-muted-foreground py-6">Sin ventas en este rango.</td></tr>
                    ) : (
                      breakdownDlg.data.rows.map((r, i) => (
                        <tr key={`${r.product_id}-${r.commission_per_unit}`} className={i % 2 ? 'bg-slate-50' : ''} data-testid={`breakdown-row-${r.product_id}`}>
                          <td className="px-3 py-2">{r.product_name}</td>
                          <td className="px-3 py-2 text-right">{formatMoney(r.commission_per_unit)}</td>
                          <td className="px-3 py-2 text-right font-semibold">{r.quantity}</td>
                          <td className="px-3 py-2 text-right font-bold text-emerald-700">{formatMoney(r.total_commission)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {breakdownDlg.data.rows.length > 0 && (
                    <tfoot className="bg-emerald-50 border-t-2 border-emerald-200">
                      <tr>
                        <td className="px-3 py-2 font-bold">TOTAL</td>
                        <td className="px-3 py-2 text-right">—</td>
                        <td className="px-3 py-2 text-right font-bold">{breakdownDlg.data.totals.total_quantity}</td>
                        <td className="px-3 py-2 text-right font-bold text-emerald-700 text-lg">
                          {formatMoney(breakdownDlg.data.totals.total_commission)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};
